import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { parseJsonResponse } from '../api/apiClient';
import { useAuth } from './AuthContext';

type IframeAuthStatus = 'idle' | 'requesting' | 'authenticating' | 'success' | 'error';

interface IframeAuthContextType {
  isIframeMode: boolean;
  iframeAuthStatus: IframeAuthStatus;
  iframeError: string | null;
  initializeIframeAuth: () => Promise<void>;
  isReauthenticating: boolean;
}

const IframeAuthContext = createContext<IframeAuthContextType | undefined>(undefined);

export const IframeAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hydrateFromTrustedLogin, logout, isAuthenticated, user } = useAuth();
  
  function originMatchesPatterns(origin: string, patterns: string[]): boolean {
    try {
      const originUrl = new URL(origin);
      const originScheme = originUrl.protocol;
      const originHost = originUrl.hostname.toLowerCase();

      return patterns.some((pattern) => {
        try {
          // Replace * with placeholder so URL constructor doesn't URL-encode it to %2A
          const normalizedPattern = pattern.replace('*.', '__WILDCARD__.');
          const patternUrl = new URL(normalizedPattern);
          
          // Protocol must match
          if (patternUrl.protocol !== originScheme) return false;
          
          const patternHost = patternUrl.hostname.toLowerCase();
          
          // Check if it's a wildcard pattern
          if (patternHost.startsWith('__wildcard__.')) {
            const baseDomain = patternHost.replace('__wildcard__.', '');
            // Must end with .baseDomain and not be exactly baseDomain (true subdomain only)
            return originHost.endsWith('.' + baseDomain) && originHost !== baseDomain;
          }
          
          // Exact match
          return originUrl.host === patternUrl.host;
        } catch {
          return false;
        }
      });
    } catch {
      return false;
    }
  }
  
  // Initialize iframe mode detection immediately
  const iframeEnabled = process.env.REACT_APP_IFRAME_ENABLED === 'true';
  const inIframe = window.self !== window.top;
  const shouldUseIframeMode = iframeEnabled && inIframe;
  
  const [isIframeMode] = useState(shouldUseIframeMode);
  const [iframeAuthStatus, setIframeAuthStatus] = useState<IframeAuthStatus>('idle');
  const [iframeError, setIframeError] = useState<string | null>(null);
  
  // Track if we need to re-initialize after logout
  const [needsReinit, setNeedsReinit] = useState(false);
  const [isReauthenticating, setIsReauthenticating] = useState(false);

  const initializeIframeAuth = useCallback(async () => {
    if (!isIframeMode || iframeAuthStatus !== 'idle') return;

    try {
      setIframeAuthStatus('requesting');
      setIframeError(null);

      const trustedOrigins = (process.env.REACT_APP_TRUSTED_PARENT_ORIGINS || '')
        .split(',')
        .map((origin) => origin.trim())
        .filter(Boolean);

      if (!trustedOrigins.length) {
        throw new Error('No trusted parent origins configured');
      }

      let timeoutId: number | undefined;
      let authCompleted = false;

      const cleanup = () => {
        window.removeEventListener('message', handleAuthMessage);
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      };

      const handleAuthMessage = async (event: MessageEvent) => {
        if (!originMatchesPatterns(event.origin, trustedOrigins)) {
          console.warn('Received message from untrusted origin:', event.origin);
          return;
        }

        try {
          if (event.data?.type === 'NEXUS_AUTH' && event.data.authData) {
            setIframeAuthStatus('authenticating');

            const {
              username,
              tenant,
              authSignature,
              timestamp,
              parentOrigin,
              displayName,
              roles,
              autoProvision,
            } = event.data.authData;

            if (!username || !tenant || !authSignature || !timestamp || !parentOrigin) {
              throw new Error('Missing required authentication data');
            }

            // Validate timestamp freshness to prevent replay attacks
            // Allow up to 5 minutes of clock drift
            const MAX_TIMESTAMP_AGE_MS = 5 * 60 * 1000;
            const timestampMs = typeof timestamp === 'number' ? timestamp : parseInt(timestamp, 10);
            const now = Date.now();
            if (isNaN(timestampMs) || Math.abs(now - timestampMs) > MAX_TIMESTAMP_AGE_MS) {
              throw new Error('Authentication timestamp is invalid or expired');
            }

            const normalizedRoles = Array.isArray(roles) ? roles : [];
            // Default autoProvision to false for security - require explicit opt-in
            const provisionFlag = typeof autoProvision === 'boolean' ? autoProvision : false;

            const payload = {
              username,
              tenant,
              auth_signature: authSignature,
              timestamp,
              parent_origin: parentOrigin,
              display_name: displayName,
              roles: normalizedRoles,
              auto_provision: provisionFlag,
            };
            
            const response = await fetch('/api/auth/trusted-login', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(payload),
            });

            if (!response.ok) {
              const errorPayload = await parseJsonResponse(response);
              throw new Error(errorPayload.error || 'Authentication failed');
            }

            const data = await parseJsonResponse(response);

            hydrateFromTrustedLogin({
              user: data.user,
              csrfToken: data.csrf_token,
            });

            authCompleted = true;
            setIframeAuthStatus('success');
            setIsReauthenticating(false);
            window.parent.postMessage({ type: 'NEXUS_AUTH_SUCCESS', user: data.user }, event.origin);
            cleanup();
          } else if (event.data?.type === 'NEXUS_AUTH_ERROR') {
            throw new Error(event.data.error || 'Authentication error from parent');
          } else {
            // Message from trusted origin but not an auth-related message - ignore it
            console.debug('[IframeAuth] Ignoring non-auth message from trusted origin:', event.data?.type);
          }
        } catch (error) {
          console.error('Iframe authentication error:', error);
          const message = error instanceof Error ? error.message : 'Authentication failed';
          authCompleted = true;
          setIframeError(message);
          setIframeAuthStatus('error');
          setIsReauthenticating(false);
          window.parent.postMessage({ type: 'NEXUS_AUTH_ERROR', error: message }, event.origin);
          cleanup();
        }
      };

      window.addEventListener('message', handleAuthMessage);

      timeoutId = window.setTimeout(() => {
        if (!authCompleted) {
          setIframeError('Authentication request timeout');
          setIframeAuthStatus('error');
          setIsReauthenticating(false);
        }
        cleanup();
      }, 10000);

      // Send auth request to each trusted origin (not wildcard) for security
      // The legitimate parent will respond; others will ignore the message
      trustedOrigins.forEach(origin => {
        try {
          // Handle wildcard patterns by extracting the base origin
          const normalizedOrigin = origin.replace('*.', 'wildcard.');
          const url = new URL(normalizedOrigin);
          const targetOrigin = url.origin.replace('wildcard.', '*.');
          window.parent.postMessage({ type: 'NEXUS_AUTH_REQUEST' }, targetOrigin);
        } catch {
          // Skip invalid origins
        }
      });
    } catch (error) {
      console.error('Failed to initialize iframe auth:', error);
      setIframeError(error instanceof Error ? error.message : 'Initialization failed');
      setIframeAuthStatus('error');
    }
  }, [hydrateFromTrustedLogin, iframeAuthStatus, isIframeMode]);

  // Check if we need to re-authenticate based on URL parameters
  // Only run this once when forceReauth is set
  useEffect(() => {
    if (!isIframeMode || isReauthenticating || needsReinit) return;
    
    const params = new URLSearchParams(window.location.search);
    const requestedUser = params.get('user');
    const forceReauth = params.get('forceReauth') === 'true';
    
    // Only proceed if forceReauth is explicitly set
    if (!forceReauth) return;
    
    console.log('[IframeAuth] Force re-auth requested:', {
      isAuthenticated,
      currentUser: user?.username,
      requestedUser,
      forceReauth
    });
    
    // If authenticated and user doesn't match, logout and re-authenticate
    if (isAuthenticated && requestedUser && user?.username !== requestedUser) {
      console.log('[IframeAuth] Starting re-authentication process for user:', requestedUser);
      setNeedsReinit(true);
      setIsReauthenticating(true);
      setIframeAuthStatus('requesting');
      logout({ skipNavigation: true }).then(() => {
        console.log('[IframeAuth] Logout complete, resetting for new auth');
        setIframeAuthStatus('idle');
        setIframeError(null);
      });
    }
  }, [isIframeMode, isAuthenticated, user, logout, isReauthenticating, needsReinit]);

  // Re-initialize iframe auth after logout
  useEffect(() => {
    if (needsReinit && !isAuthenticated && iframeAuthStatus === 'idle') {
      setNeedsReinit(false);
      initializeIframeAuth().finally(() => {
        setIsReauthenticating(false);
      });
    }
  }, [needsReinit, isAuthenticated, iframeAuthStatus, initializeIframeAuth]);

  const value = useMemo(
    () => ({
      isIframeMode,
      iframeAuthStatus,
      iframeError,
      initializeIframeAuth,
      isReauthenticating,
    }),
    [iframeAuthStatus, iframeError, initializeIframeAuth, isIframeMode, isReauthenticating],
  );

  return <IframeAuthContext.Provider value={value}>{children}</IframeAuthContext.Provider>;
};

export const useIframeAuth = (): IframeAuthContextType => {
  const context = useContext(IframeAuthContext);
  if (!context) {
    throw new Error('useIframeAuth must be used within an IframeAuthProvider');
  }

  return context;
};
